import { useTranslations } from "use-intl";

import { OpenExternalIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import type { OfferRowProps } from "@/components/marketplace/offer-row";
import { OfferConditionDetails } from "@/components/marketplace/OfferConditionDetails";
import { useOfferMediaPacking } from "@/components/marketplace/useOfferMediaPacking";
import { Price } from "@/components/Price";
import { useRastrPurchaseHref } from "@/hooks/useRastrPurchaseHref";
import { trackOfferClicked } from "@/lib/analytics";

export function UsedOfferRow({
  offer,
  currency,
  contextReleaseId,
  listingDetails,
  editionContext,
  adminAction,
}: OfferRowProps) {
  const t = useTranslations("shared.affiliates");
  const packing = useOfferMediaPacking(offer.releaseMediaFormatSummary, offer.formatCategory);
  const href = useRastrPurchaseHref(offer.linkType ? offer.purchaseUrl : null);
  return (
    <li
      data-offer-id={offer.offerId}
      className="border-b border-border/60 px-4 py-3 last:border-b-0 sm:px-6"
    >
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold [overflow-wrap:anywhere]">
            {offer.sellerName || offer.providerDisplayName}
          </p>
          <p className="text-xs leading-snug text-muted-foreground [overflow-wrap:anywhere]">
            {offer.title}
          </p>
          <OfferConditionDetails details={offer.conditionDetails} />
        </div>
        <div className="flex flex-col items-end gap-1">
          <Price
            amount={offer.currentPrice}
            currency={offer.currentCurrency ?? currency}
            className="text-base font-bold tabular-nums"
          />
          <span className="inline-flex min-w-0 items-center gap-1.5 text-xs font-normal text-muted-foreground">
            {offer.providerIconUrl && (
              <img
                src={offer.providerIconUrl}
                alt=""
                loading="lazy"
                className="size-5 shrink-0 rounded object-contain"
              />
            )}
            {offer.providerDisplayName}
          </span>
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener sponsored"
              onClick={trackOfferClicked}
              aria-label={`${t("viewOffer")} · ${offer.providerDisplayName}`}
              className="inline-flex min-h-8 items-center justify-end gap-1 rounded text-xs text-primary underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-primary pointer-coarse:min-h-11"
            >
              {t("viewOffer")}
              <OpenExternalIcon aria-hidden="true" className="text-sm" />
            </a>
          ) : (
            <span className="py-2 text-xs text-muted-foreground">{t("linkPending")}</span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-start gap-x-3 text-xs text-muted-foreground">
        {offer.matchCatalogId && offer.matchCatalogId !== contextReleaseId ? (
          <Link
            to="/release/$id"
            params={{ id: offer.matchCatalogId }}
            className="inline-flex min-h-8 items-center rounded hover:text-primary hover:underline pointer-coarse:min-h-11"
          >
            {listingDetails ?? packing} · {t("offerViewEdition")}
          </Link>
        ) : (
          <span>{listingDetails ?? packing}</span>
        )}
        {editionContext}
        {adminAction}
      </div>
    </li>
  );
}
